import type { BroadcastingService } from '@/types/services/broadcasting/BroadcastingService.ts'
import { EchoService } from '@/config/services/broadcasting/EchoService.ts'

const debugLog = (message: string) => {
  if (localStorage?.broadcastDebug !== '1') return
  console.log(`[BroadcastingServiceProvider] ${message}`)
}

/**
 * Factory and registry for broadcasting services.
 * Manages the lifecycle of broadcasting services and allows switching between
 * different implementations based on school configuration.
 */
class BroadcastingServiceProvider {
  private static instance: BroadcastingServiceProvider | null = null
  private currentService: BroadcastingService | null = null

  public static getInstance(): BroadcastingServiceProvider {
    if (!BroadcastingServiceProvider.instance) {
      BroadcastingServiceProvider.instance = new BroadcastingServiceProvider()
    }
    return BroadcastingServiceProvider.instance
  }

  /**
   * Get the current broadcasting service or create one if a type is provided.
   *
   * @overload getService() - Returns current service or null if none exists
   * @overload getService(type) - Returns existing service or creates a new one of the specified type
   */
  public getService(): BroadcastingService | null
  public getService(type: string): BroadcastingService
  public getService(type?: string): BroadcastingService | null {
    if (type === undefined && !this.currentService) {
      // TODO: catch this in sentry with enriched data populated from the
      //       calling handlers. Opportunity to introduce a handler interface.
      debugLog('No cached Broadcasting Service provided.')
      return null
    }
    // Return the cached instance if method called without a type.
    if (type === undefined) return this.currentService
    // Return the cached instance if one already exists.
    // TODO: switching between providers is not supported and outside the scope - yet.
    if (this.currentService) return this.currentService

    // Instantiate a new service if a type is provided.
    this.currentService = this.createService(type)
    debugLog(`Created new service of type: ${type}`)
    return this.currentService
  }

  /**
   * Stop the current service and clear state.
   */
  public stopCurrent(): void {
    if (this.currentService) {
      debugLog('Stopping current Broadcasting Service')
      this.currentService.stop()
    }
    this.currentService = null
  }

  private createService(type?: string): BroadcastingService {
    switch (type) {
    default:
      return EchoService.getInstance()
    }
  }
}

export default BroadcastingServiceProvider.getInstance()

